/**
 * "HTML 다운로드" 버튼용 훅 — blob 생성/다운로드, 로딩 상태, 통계 로깅, 에러 처리를
 * 캡슐화한다. 호출부는 build() 안에서 데이터를 모아 HTML 문자열만 만들면 된다.
 *
 * build() 는 무거운 뷰어 번들을 다운로드 시점에만 동적 import 하기 좋은 자리다
 * (정적 import 하면 초기 번들이 커진다 — 동적 import 로 별도 청크 분리 유지).
 */

import { useCallback, useRef, useState } from "react";

import { downloadBlob } from "@/utils/download-blob";

export interface HtmlExportResult {
  /** buildStandaloneHtml() 등으로 만든 self-contained HTML 문자열. */
  html: string;
  /** 저장 파일명(확장자 포함). NFC 정규화는 downloadBlob 이 처리. */
  filename: string;
}

export interface UseHtmlExportOptions {
  /**
   * HTML 과 파일명을 만든다. 데이터가 없는 등 중단해야 하면 null 을 반환(조용히 종료).
   * 무거운 builder/뷰어 번들은 여기서 동적 import 할 것.
   */
  build: () => Promise<HtmlExportResult | null>;
  /** 다운로드 성공 직후 호출(통계 집계 등). 실패해도 다운로드엔 영향 없게 fire-and-forget 로 쓸 것. */
  onLogged?: () => void;
  /** build/다운로드 중 throw 시 호출(토스트 등). 미지정 시 에러는 조용히 삼킨다. */
  onError?: (error: unknown) => void;
}

/**
 * @returns exportNow 는 안정적인 reference 이며 항상 최신 옵션을 읽는다.
 *          isExporting 으로 버튼 로딩/비활성 상태를 제어한다.
 */
export function useHtmlExport(options: UseHtmlExportOptions): {
  exportNow: () => Promise<void>;
  isExporting: boolean;
} {
  const [isExporting, setIsExporting] = useState(false);
  // 최신 옵션을 ref 로 들고 있어, exportNow 를 안정 reference 로 유지하면서도 stale 클로저를 피한다.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  // 진행 중 중복 호출(더블클릭 등)을 isExporting state 보다 빠르게 차단.
  const inFlightRef = useRef(false);

  const exportNow = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsExporting(true);
    try {
      const result = await optionsRef.current.build();
      if (!result) return;
      downloadBlob(
        new Blob([result.html], { type: "text/html;charset=utf-8" }),
        result.filename,
      );
      optionsRef.current.onLogged?.();
    } catch (error) {
      optionsRef.current.onError?.(error);
    } finally {
      inFlightRef.current = false;
      setIsExporting(false);
    }
  }, []);

  return { exportNow, isExporting };
}
